import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  upsertApprovedMLS,
  deleteApprovedMLS as deleteApprovedMLSApi,
} from '../../../api/mls';
import { approvedMLSKeys } from './useApprovedMLSList';

/**
 * Input for adding/updating an approved MLS entry
 */
interface ApprovedMLSInput {
  boardCode: string;
  mlsName: string;
  isApproved: boolean;
}

/**
 * Add or update an approved MLS entry
 * Uses the boardCode as the document ID
 */
const addOrUpdateApprovedMLS = async (input: ApprovedMLSInput): Promise<void> => {
  // Source swapped Firestore MLSLists -> Spanner via the mls service; shape unchanged.
  await upsertApprovedMLS(input);
};

/**
 * Delete an approved MLS entry
 */
const deleteApprovedMLS = async (boardCode: string): Promise<void> => {
  await deleteApprovedMLSApi(boardCode);
};

/**
 * Hook to add or update an approved MLS entry
 */
export const useAddApprovedMLS = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addOrUpdateApprovedMLS,
    onSuccess: () => {
      // Invalidate the approved MLS list to trigger a refetch
      queryClient.invalidateQueries({ queryKey: approvedMLSKeys.list() });
    },
  });
};

/**
 * Hook to update an approved MLS entry's approval status
 */
export const useUpdateApprovedMLS = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addOrUpdateApprovedMLS,
    onSuccess: () => {
      // Invalidate the approved MLS list to trigger a refetch
      queryClient.invalidateQueries({ queryKey: approvedMLSKeys.list() });
    },
  });
};

/**
 * Hook to delete an approved MLS entry
 */
export const useDeleteApprovedMLS = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteApprovedMLS,
    onSuccess: () => {
      // Invalidate the approved MLS list to trigger a refetch
      queryClient.invalidateQueries({ queryKey: approvedMLSKeys.list() });
    },
  });
};

/**
 * Bulk add MLS entries
 */
const bulkAddApprovedMLS = async (
  entries: ApprovedMLSInput[]
): Promise<{ added: number; skipped: number }> => {
  let added = 0;
  let skipped = 0;

  for (const entry of entries) {
    try {
      await upsertApprovedMLS(entry);
      added++;
    } catch {
      skipped++;
    }
  }

  return { added, skipped };
};

/**
 * Hook to bulk add MLS entries
 */
export const useBulkAddApprovedMLS = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: bulkAddApprovedMLS,
    onSuccess: () => {
      // Invalidate the approved MLS list to trigger a refetch
      queryClient.invalidateQueries({ queryKey: approvedMLSKeys.list() });
    },
  });
};
